import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, formatDateTime } from '../styles';
import { sendEmail } from '../utils/emailService';
import { dayworkSubmissionEmail, variationSubmissionEmail, dayworkAgreementEmail, variationAgreementEmail, accountApprovedEmail, mcAccessApprovedEmail, mcAccessRejectedEmail } from '../utils/emailTemplates';

const ROLE_LABELS = {
  admin: 'Admin',
  proline_sm: 'Site Manager',
  proline_qs: 'Proline QS',
  mc_qs: 'MC QS',
};

function RoleBadge({ role }) {
  const colors = {
    admin: { background: '#fef3c7', color: '#92400e' },
    proline_sm: { background: '#dbeafe', color: '#1e40af' },
    proline_qs: { background: '#d1fae5', color: '#065f46' },
    mc_qs: { background: '#f3e8ff', color: '#6b21a8' },
  };
  return (
    <span style={{
      ...S.badge,
      ...(colors[role] || { background: '#f3f4f6', color: '#374151' }),
    }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function ApprovedBadge({ approved }) {
  return (
    <span style={{
      ...S.badge,
      ...(approved
        ? { background: C.successLight, color: C.success }
        : { background: C.warningLight, color: C.warning }),
    }}>
      {approved ? 'Approved' : 'Pending'}
    </span>
  );
}

// --- Pending Approvals Tab ---
function PendingApprovalsTab() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [assignMap, setAssignMap] = useState({});
  const [processing, setProcessing] = useState({});
  const [message, setMessage] = useState('');
  // mc_qs assign modal
  const [mcModal, setMcModal] = useState(null); // { user, siteNameGiven, projectId, saving }
  // reject modal
  const [rejectModal, setRejectModal] = useState(null); // { user, reason, saving }

  useEffect(() => {
    loadPending();
    loadProjects();
  }, []);

  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function loadPending() {
    setLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('approved', false)
      .order('created_at', { ascending: false });
    setPending(data || []);
    setLoading(false);
  }

  async function loadProjects() {
    const { data } = await supabase
      .from('projects')
      .select('id, name, reference')
      .eq('active', true)
      .order('name');
    setProjects(data || []);
  }

  function openMcApproveModal(user) {
    setMcModal({ user, siteNameGiven: user.site_name || '', projectId: '', saving: false });
  }

  async function handleMcApproveConfirm() {
    if (!mcModal.projectId) return;
    setMcModal(m => ({ ...m, saving: true }));
    const { user, projectId } = mcModal;
    try {
      const project = projects.find(p => p.id === projectId);

      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ approved: true, requested_project_id: projectId })
        .eq('id', user.id);
      if (profileErr) throw new Error('Profile update failed: ' + profileErr.message);

      const { error: puErr } = await supabase
        .from('project_users')
        .upsert(
          { project_id: projectId, user_id: user.id, role: 'mc_qs' },
          { onConflict: 'project_id,user_id' }
        );
      if (puErr) throw new Error('Project assignment failed: ' + puErr.message);

      if (user.email) {
        try {
          const { subject, htmlBody } = mcAccessApprovedEmail(user.full_name, project?.name);
          await sendEmail({ to: user.email, subject, htmlBody });
        } catch (emailErr) {
          console.error('MC approval email failed:', emailErr);
        }
      }
      setMessage(`Approved ${user.full_name} and assigned to ${project?.name}`);
      setMcModal(null);
      loadPending();
    } catch (err) {
      setMcModal(m => ({ ...m, saving: false }));
      setMessage('Error: ' + err.message);
    }
  }

  async function handleApprove(user) {
    if (user.role === 'mc_qs') { openMcApproveModal(user); return; }
    setProcessing(p => ({ ...p, [user.id]: true }));
    setMessage('');
    try {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ approved: true })
        .eq('id', user.id);
      if (profileErr) throw new Error('Profile update failed: ' + profileErr.message);

      const projectId = assignMap[user.id] || user.requested_project_id;
      if (projectId) {
        const { error: puErr } = await supabase
          .from('project_users')
          .upsert(
            { project_id: projectId, user_id: user.id, role: user.role },
            { onConflict: 'project_id,user_id' }
          );
        if (puErr) throw new Error('Project assignment failed: ' + puErr.message);
      }
      if (user.email) {
        try {
          const { subject, htmlBody } = accountApprovedEmail(user.full_name);
          await sendEmail({ to: user.email, subject, htmlBody });
        } catch (emailErr) {
          console.error('Approval email failed:', emailErr);
        }
      }
      setMessage(`Approved ${user.full_name}`);
      loadPending();
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setProcessing(p => ({ ...p, [user.id]: false }));
    }
  }

  function openRejectModal(user) {
    setRejectModal({ user, reason: '', saving: false });
  }

  async function handleRejectConfirm() {
    const { user, reason } = rejectModal;
    setRejectModal(m => ({ ...m, saving: true }));
    try {
      if (user.email && reason.trim()) {
        try {
          const { subject, htmlBody } = mcAccessRejectedEmail(user.full_name, reason.trim());
          await sendEmail({ to: user.email, subject, htmlBody });
        } catch (emailErr) {
          console.error('Rejection email failed:', emailErr);
        }
      }
      await supabase.from('profiles').delete().eq('id', user.id);
      setMessage(`Rejected ${user.full_name}`);
      setRejectModal(null);
      loadPending();
    } catch (err) {
      setRejectModal(m => ({ ...m, saving: false }));
      setMessage('Error: ' + err.message);
    }
  }

  if (loading) return <div style={S.spinner}>Loading...</div>;
  if (error) return <div style={{ padding: '20px' }}><div style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</div><button onClick={() => window.location.reload()} style={{ padding: '8px 16px', background: '#374151', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Refresh Page</button></div>;

  return (
    <div>
      {message && <div style={S.alertSuccess}>{message}</div>}

      {/* MC QS Assign & Approve Modal */}
      {mcModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ ...S.card, maxWidth: '480px', width: '100%', padding: '28px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 4px' }}>Assign & Approve MC User</h3>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 20px' }}>
              Assign <strong>{mcModal.user.full_name}</strong> ({mcModal.user.company}) to a project before approving.
            </p>
            {mcModal.siteNameGiven && (
              <div style={{ background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: C.textMuted }}>
                Site name given at registration: <strong style={{ color: C.text }}>{mcModal.siteNameGiven}</strong>
              </div>
            )}
            <div style={S.formGroup}>
              <label style={S.label}>Project <span style={{ color: C.danger }}>*</span></label>
              <select
                value={mcModal.projectId}
                onChange={e => setMcModal(m => ({ ...m, projectId: e.target.value }))}
                style={S.select}
              >
                <option value="">— Select project —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setMcModal(null)} style={{ ...S.btn, ...S.btnGhost }} disabled={mcModal.saving}>Cancel</button>
              <button
                onClick={handleMcApproveConfirm}
                disabled={!mcModal.projectId || mcModal.saving}
                style={{ ...S.btn, ...S.btnSuccess, opacity: (!mcModal.projectId || mcModal.saving) ? 0.7 : 1 }}
              >
                {mcModal.saving ? 'Approving...' : 'Assign & Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ ...S.card, maxWidth: '440px', width: '100%', padding: '28px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 4px' }}>Reject Access Request</h3>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 16px' }}>
              Rejecting <strong>{rejectModal.user.full_name}</strong>. Optionally provide a reason — this will be emailed to them.
            </p>
            <div style={S.formGroup}>
              <label style={S.label}>Reason (optional)</label>
              <textarea
                style={{ ...S.textarea, minHeight: '80px' }}
                value={rejectModal.reason}
                onChange={e => setRejectModal(m => ({ ...m, reason: e.target.value }))}
                placeholder="e.g. Unable to verify your association with this project."
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setRejectModal(null)} style={{ ...S.btn, ...S.btnGhost }} disabled={rejectModal.saving}>Cancel</button>
              <button
                onClick={handleRejectConfirm}
                disabled={rejectModal.saving}
                style={{ ...S.btn, ...S.btnDanger, opacity: rejectModal.saving ? 0.7 : 1 }}
              >
                {rejectModal.saving ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pending.length === 0 ? (
        <div style={S.emptyState}>
          <div style={S.emptyStateText}>No pending approvals</div>
          <div style={S.emptyStateSubtext}>All users are approved</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {pending.map(user => {
            const isMcQsUser = user.role === 'mc_qs';
            return (
              <div key={user.id} style={{ ...S.card, padding: '16px 20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>{user.full_name}</div>
                    <div style={{ fontSize: '13px', color: C.textMuted, marginBottom: '2px' }}>{user.email}</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                      <RoleBadge role={user.role} />
                      {user.company && (
                        <span style={{ ...S.badge, background: '#f3f4f6', color: C.textMuted }}>{user.company}</span>
                      )}
                      {user.phone && (
                        <span style={{ fontSize: '12px', color: C.textMuted }}>{user.phone}</span>
                      )}
                    </div>
                    {user.site_name && (
                      <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                        Site: {user.site_name}
                      </div>
                    )}
                    {user.requested_project_id && (
                      <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                        Requested project: {projects.find(p => p.id === user.requested_project_id)?.name || user.requested_project_id}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
                    {!isMcQsUser && (
                      <select
                        value={assignMap[user.id] || user.requested_project_id || ''}
                        onChange={e => setAssignMap(m => ({ ...m, [user.id]: e.target.value }))}
                        style={{ ...S.select, fontSize: '13px' }}
                      >
                        <option value="">Assign to project (optional)</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>
                        ))}
                      </select>
                    )}
                    {isMcQsUser && (
                      <div style={{ fontSize: '12px', color: C.textMuted, background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: '5px', padding: '8px 10px' }}>
                        MC user — project assignment required on approval
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleApprove(user)}
                        disabled={processing[user.id]}
                        style={{ ...S.btn, ...S.btnSuccess, flex: 1, justifyContent: 'center', opacity: processing[user.id] ? 0.7 : 1 }}
                      >
                        {isMcQsUser ? 'Assign & Approve' : 'Approve'}
                      </button>
                      <button
                        onClick={() => openRejectModal(user)}
                        disabled={processing[user.id]}
                        style={{ ...S.btn, ...S.btnDanger, flex: 1, justifyContent: 'center', opacity: processing[user.id] ? 0.7 : 1 }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- All Users Tab ---
function AllUsersTab() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editModal, setEditModal] = useState(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: usersData }, { data: projectsData }] = await Promise.all([
      supabase.from('profiles').select('*').order('full_name'),
      supabase.from('projects').select('id, name, reference').eq('active', true).order('name'),
    ]);
    setUsers(usersData || []);
    setProjects(projectsData || []);
    setLoading(false);
  }

  function flash(msg) { setMessage(msg); setTimeout(() => setMessage(''), 4000); }

  function openEditModal(user) {
    setEditModal({
      user,
      originalEmail: user.email,
      form: {
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        role: user.role || 'proline_sm',
        requested_project_id: user.requested_project_id || '',
        approved: user.approved !== false,
      },
      saving: false,
      error: '',
      emailWarning: false,
    });
  }

  async function handleEditSave() {
    if (!editModal) return;
    const { user, form, originalEmail } = editModal;

    if (!form.full_name.trim()) {
      setEditModal(m => ({ ...m, error: 'Full name is required.' }));
      return;
    }

    const emailChanged = form.email !== originalEmail;
    if (emailChanged) {
      setEditModal(m => ({ ...m, emailWarning: true, form: { ...m.form, email: originalEmail } }));
    }

    setEditModal(m => ({ ...m, saving: true, error: '' }));

    try {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({
          full_name: form.full_name.trim(),
          phone: form.phone.trim() || null,
          company: form.company.trim() || null,
          role: form.role,
          requested_project_id: form.requested_project_id || null,
          approved: form.approved,
        })
        .eq('id', user.id);
      if (profileErr) throw new Error('Profile update failed: ' + profileErr.message);

      const newProjectId = form.requested_project_id || null;
      const oldProjectId = user.requested_project_id || null;

      if (newProjectId) {
        if (oldProjectId && oldProjectId !== newProjectId) {
          await supabase.from('project_users').delete().eq('user_id', user.id).eq('project_id', oldProjectId);
        }
        const { error: puErr } = await supabase.from('project_users').upsert(
          { project_id: newProjectId, user_id: user.id, role: form.role },
          { onConflict: 'project_id,user_id' }
        );
        if (puErr) console.error('project_users upsert failed:', puErr);
      } else if (oldProjectId) {
        await supabase.from('project_users').delete().eq('user_id', user.id).eq('project_id', oldProjectId);
      }

      const wasDeactivated = !user.approved;
      if (wasDeactivated && form.approved && user.email) {
        try {
          if (form.role === 'mc_qs') {
            const project = projects.find(p => p.id === newProjectId);
            const { subject, htmlBody } = mcAccessApprovedEmail(form.full_name.trim(), project?.name);
            await sendEmail({ to: user.email, subject, htmlBody });
          } else {
            const { subject, htmlBody } = accountApprovedEmail(form.full_name.trim());
            await sendEmail({ to: user.email, subject, htmlBody });
          }
        } catch (e) { console.error('Reactivation email failed:', e); }
      }

      setEditModal(null);
      flash(`User ${form.full_name.trim()} updated successfully`);
      loadData();
    } catch (err) {
      setEditModal(m => ({ ...m, saving: false, error: err.message }));
    }
  }

  if (loading) return <div style={S.spinner}>Loading...</div>;

  return (
    <div>
      {message && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, ...S.alertSuccess, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {message}
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ ...S.card, maxWidth: '560px', width: '100%', padding: '28px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '17px', fontWeight: '600', margin: '0 0 20px' }}>Edit User — {editModal.user.full_name}</h3>

            {editModal.error && <div style={S.alertError}>{editModal.error}</div>}
            {editModal.emailWarning && (
              <div style={S.alertWarning}>
                Email changes are not supported here. Please use Supabase Studio to change a user's email address.
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ ...S.formGroup, gridColumn: '1 / -1' }}>
                <label style={S.label}>Full Name <span style={{ color: C.danger }}>*</span></label>
                <input style={S.input} value={editModal.form.full_name} onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, full_name: e.target.value } }))} />
              </div>

              <div style={{ ...S.formGroup, gridColumn: '1 / -1' }}>
                <label style={S.label}>Email Address</label>
                <input
                  style={S.input} type="email" value={editModal.form.email}
                  onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, email: e.target.value }, emailWarning: false }))}
                />
                <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                  Changing email requires the user to re-verify. Use with caution.
                </div>
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Phone</label>
                <input style={S.input} value={editModal.form.phone} onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, phone: e.target.value } }))} placeholder="Optional" />
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Company</label>
                <input style={S.input} value={editModal.form.company} onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, company: e.target.value } }))} placeholder="Optional" />
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Role <span style={{ color: C.danger }}>*</span></label>
                <select style={S.select} value={editModal.form.role} onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, role: e.target.value } }))}>
                  <option value="admin">Admin</option>
                  <option value="proline_sm">Site Manager</option>
                  <option value="proline_qs">QS</option>
                  <option value="mc_qs">MC QS</option>
                </select>
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Assigned Project</label>
                <select style={S.select} value={editModal.form.requested_project_id} onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, requested_project_id: e.target.value } }))}>
                  <option value="">— None —</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                  For MC QS users this controls which project they see in their portal.
                </div>
              </div>

              <div style={{ ...S.formGroup, gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '14px', color: C.text }}>
                  <input
                    type="checkbox" checked={editModal.form.approved}
                    onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, approved: e.target.checked } }))}
                    style={{ width: '16px', height: '16px' }}
                  />
                  Account Active
                </label>
                {!editModal.form.approved && (
                  <div style={{ fontSize: '12px', color: C.warning, marginTop: '4px' }}>
                    User will see "Account Pending Approval" until reactivated.
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setEditModal(null)} disabled={editModal.saving} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
              <button onClick={handleEditSave} disabled={editModal.saving} style={{ ...S.btn, background: '#111827', color: '#fff', opacity: editModal.saving ? 0.7 : 1 }}>
                {editModal.saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={S.tableWrapper}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Company</th>
              <th style={S.th}>Project</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: C.textMuted }}>No users found</td></tr>
            ) : users.map(u => {
              const assignedProject = u.requested_project_id ? projects.find(p => p.id === u.requested_project_id) : null;
              return (
                <tr key={u.id}>
                  <td style={S.td}><span style={{ fontWeight: '500' }}>{u.full_name}</span></td>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}><RoleBadge role={u.role} /></td>
                  <td style={S.td}>{u.company || '—'}</td>
                  <td style={S.td}>{u.role === 'mc_qs' ? (assignedProject?.name || '—') : '—'}</td>
                  <td style={S.td}><ApprovedBadge approved={u.approved} /></td>
                  <td style={S.td}>
                    <button onClick={() => openEditModal(u)} style={{ ...S.btn, ...S.btnSm, background: '#111827', color: '#fff', border: 'none' }}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Projects Tab ---
function ProjectsTab({ navigate }) {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [qsToast, setQsToast] = useState('');
  const [form, setForm] = useState({
    name: '', reference: '', project_code: '', address: '', main_contractor: '',
    contract_value: '', start_date: '', scope: '', daywork_seq_start: '',
  });

  useEffect(() => {
    loadProjects();
    loadAllUsers();
  }, []);

  async function loadProjects() {
    setLoading(true);
    const { data } = await supabase
      .from('projects')
      .select('*, qs_user_id')
      .order('created_at', { ascending: false });
    setProjects(data || []);
    setLoading(false);
  }

  async function loadAllUsers() {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .eq('approved', true)
      .order('full_name');
    setAllUsers(data || []);
  }

  async function handleQsAssign(project, userId) {
    await supabase.from('projects').update({ qs_user_id: userId || null }).eq('id', project.id);
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, qs_user_id: userId || null } : p));
    const user = allUsers.find(u => u.id === userId);
    setQsToast(user ? `QS assigned to ${project.name}` : `QS assignment cleared for ${project.name}`);
    setTimeout(() => setQsToast(''), 3000);
  }

  function updateForm(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    const code = form.project_code.trim().toUpperCase();
    if (!form.name.trim() || !form.reference.trim()) {
      setError('Name and reference are required.');
      return;
    }
    if (!code || code.length > 6 || !/^[A-Z0-9]+$/.test(code)) {
      setError('Project Code is required: 1–6 uppercase letters/numbers (e.g. MSP, BLD1).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data, error: insertError } = await supabase
        .from('projects')
        .insert({
          name: form.name,
          reference: form.reference,
          project_code: code,
          address: form.address,
          main_contractor: form.main_contractor,
          contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
          start_date: form.start_date || null,
          scope: form.scope,
          daywork_seq_start: form.daywork_seq_start ? parseInt(form.daywork_seq_start, 10) : 1,
          created_by: profile.id,
          active: true,
        })
        .select()
        .single();
      if (insertError) throw insertError;
      await supabase.from('project_users')
        .upsert({ project_id: data.id, user_id: profile.id, role: 'creator' }, { onConflict: 'project_id,user_id' });
      setForm({ name: '', reference: '', project_code: '', address: '', main_contractor: '', contract_value: '', start_date: '', scope: '', daywork_seq_start: '' });
      setShowForm(false);
      loadProjects();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(project) {
    await supabase.from('projects').update({ active: !project.active }).eq('id', project.id);
    loadProjects();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{ ...S.btn, ...S.btnPrimary }}
        >
          {showForm ? 'Cancel' : '+ New Project'}
        </button>
      </div>

      {showForm && (
        <div style={{ ...S.card, marginBottom: '24px' }}>
          <h3 style={{ ...S.cardTitle, marginBottom: '16px' }}>Create New Project</h3>
          {error && <div style={S.alertError}>{error}</div>}
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              <div style={S.formGroup}>
                <label style={S.label}>Project Name *</label>
                <input style={S.input} value={form.name} onChange={e => updateForm('name', e.target.value)} placeholder="e.g. Riverside Development" />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Reference *</label>
                <input style={S.input} value={form.reference} onChange={e => updateForm('reference', e.target.value)} placeholder="e.g. RD-2024-001" />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Project Code * <span style={{ fontWeight: '400', color: C.textMuted }}>(max 6 chars, e.g. MSP)</span></label>
                <input
                  style={S.input}
                  value={form.project_code}
                  onChange={e => updateForm('project_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                  placeholder="e.g. MSP"
                  maxLength={6}
                />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>DW Start Number <span style={{ fontWeight: '400', color: C.textMuted }}>(if continuing paper sheets)</span></label>
                <input style={S.input} type="number" min="1" value={form.daywork_seq_start} onChange={e => updateForm('daywork_seq_start', e.target.value)} placeholder="e.g. 942 (leave blank for 1)" />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Main Contractor</label>
                <input style={S.input} value={form.main_contractor} onChange={e => updateForm('main_contractor', e.target.value)} placeholder="ABC Construction Ltd" />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Contract Value (£)</label>
                <input style={S.input} type="number" step="0.01" value={form.contract_value} onChange={e => updateForm('contract_value', e.target.value)} placeholder="0.00" />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Start Date</label>
                <input style={S.input} type="date" value={form.start_date} onChange={e => updateForm('start_date', e.target.value)} />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Address</label>
                <input style={S.input} value={form.address} onChange={e => updateForm('address', e.target.value)} placeholder="Site address" />
              </div>
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Scope of Works</label>
              <textarea style={S.textarea} value={form.scope} onChange={e => updateForm('scope', e.target.value)} placeholder="Brief description of scope" rows={3} />
            </div>
            <button type="submit" disabled={saving} style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Creating...' : 'Create Project'}
            </button>
          </form>
        </div>
      )}

      {qsToast && (
        <div style={{ background: C.successLight, color: C.success, border: `1px solid ${C.success}`, borderRadius: '6px', padding: '10px 16px', marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>
          {qsToast}
        </div>
      )}

      <div style={S.tableWrapper}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Reference</th>
              <th style={S.th}>Name</th>
              <th style={S.th}>Main Contractor</th>
              <th style={S.th}>QS / Commercial</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center' }}>Loading...</td></tr>
            ) : projects.length === 0 ? (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: C.textMuted }}>No projects</td></tr>
            ) : projects.map(p => (
              <tr key={p.id}>
                <td style={S.td}><span style={{ fontWeight: '600', color: C.accent }}>{p.reference}</span></td>
                <td style={S.td}>{p.name}</td>
                <td style={S.td}>{p.main_contractor || '—'}</td>
                <td style={S.td}>
                  <select
                    value={p.qs_user_id || ''}
                    onChange={e => handleQsAssign(p, e.target.value)}
                    style={{ ...S.input, padding: '4px 8px', fontSize: '13px', minWidth: '160px' }}
                  >
                    <option value="">— Unassigned —</option>
                    {allUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </td>
                <td style={S.td}>
                  <span style={{
                    ...S.badge,
                    ...(p.active ? { background: C.successLight, color: C.success } : { background: '#f3f4f6', color: C.textMuted }),
                  }}>
                    {p.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={S.td}>
                  <button
                    onClick={() => toggleActive(p)}
                    style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}
                  >
                    {p.active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Assignments Tab ---
function AssignmentsTab() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('proline_sm');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [{ data: projs }, { data: usrs }] = await Promise.all([
      supabase.from('projects').select('id, name, reference').eq('active', true).order('name'),
      supabase.from('profiles').select('id, full_name, email, role').eq('approved', true).order('full_name'),
    ]);
    setProjects(projs || []);
    setUsers(usrs || []);
  }

  useEffect(() => {
    if (selectedProject) loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  async function loadAssignments() {
    setLoading(true);
    const { data } = await supabase
      .from('project_users')
      .select('user_id, role, assigned_at, profiles(id, full_name, email, role)')
      .eq('project_id', selectedProject);
    setAssignments(data || []);
    setLoading(false);
  }

  async function handleAdd() {
    if (!selectedUser || !selectedProject) return;
    setAdding(true);
    setMessage('');
    try {
      await supabase.from('project_users').upsert(
        { project_id: selectedProject, user_id: selectedUser, role: selectedRole },
        { onConflict: 'project_id,user_id' }
      );
      setMessage('User assigned successfully.');
      setSelectedUser('');
      loadAssignments();
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(userId) {
    await supabase.from('project_users').delete().eq('project_id', selectedProject).eq('user_id', userId);
    loadAssignments();
  }

  return (
    <div>
      {message && <div style={S.alertSuccess}>{message}</div>}

      <div style={{ ...S.card, marginBottom: '24px' }}>
        <div style={S.formGroup}>
          <label style={S.label}>Select Project</label>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} style={S.select}>
            <option value="">— Choose project —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>
            ))}
          </select>
        </div>

        {selectedProject && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '12px' }}>
            <div style={{ flex: 2, minWidth: '180px' }}>
              <label style={S.label}>Add User</label>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={S.select}>
                <option value="">— Select user —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name} ({ROLE_LABELS[u.role] || u.role})</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '140px' }}>
              <label style={S.label}>Role on Project</label>
              <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)} style={S.select}>
                <option value="proline_sm">Site Manager</option>
                <option value="proline_qs">Proline QS</option>
                <option value="mc_qs">MC QS</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              onClick={handleAdd}
              disabled={adding || !selectedUser}
              style={{ ...S.btn, ...S.btnPrimary, opacity: (adding || !selectedUser) ? 0.7 : 1 }}
            >
              Assign
            </button>
          </div>
        )}
      </div>

      {selectedProject && (
        <div style={S.tableWrapper}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Name</th>
                <th style={S.th}>Email</th>
                <th style={S.th}>Role</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center' }}>Loading...</td></tr>
              ) : assignments.length === 0 ? (
                <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: C.textMuted }}>No users assigned</td></tr>
              ) : assignments.map(a => (
                <tr key={a.user_id}>
                  <td style={S.td}><span style={{ fontWeight: '500' }}>{a.profiles?.full_name}</span></td>
                  <td style={S.td}>{a.profiles?.email}</td>
                  <td style={S.td}><RoleBadge role={a.profiles?.role} /></td>
                  <td style={S.td}>
                    <button
                      onClick={() => handleRemove(a.user_id)}
                      style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Labour Rates Tab ---
function LabourRatesTab() {
  const { profile } = useAuth();
  const [globalRates, setGlobalRates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [editingRate, setEditingRate] = useState({});
  const [editingProjectRates, setEditingProjectRates] = useState({});
  const [settings, setSettings] = useState({ materials_uplift_pct: '25', plant_uplift_pct: '0' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: rates }, { data: projs }, { data: setts }] = await Promise.all([
      supabase.from('labour_rates').select('*').order('trade'),
      supabase.from('projects').select('id, name, reference').eq('active', true).order('name'),
      supabase.from('commercial_settings').select('*'),
    ]);
    setGlobalRates(rates || []);
    setProjects(projs || []);
    const sm = {};
    (setts || []).forEach(s => { sm[s.setting_key] = String(s.setting_value); });
    setSettings({
      materials_uplift_pct: sm['materials_uplift_pct'] ?? '25',
      plant_uplift_pct: sm['plant_uplift_pct'] ?? '0',
    });
    setLoading(false);
  }

  useEffect(() => {
    if (selectedProject) loadProjectRates();
    else setEditingProjectRates({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  async function loadProjectRates() {
    const { data } = await supabase
      .from('project_labour_rates').select('*').eq('project_id', selectedProject);
    const m = {};
    (data || []).forEach(r => { m[r.trade] = String(r.rate_per_hour); });
    setEditingProjectRates(m);
  }

  function flash(msg) {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  }

  async function saveGlobalRate(trade, rate) {
    if (!rate || isNaN(parseFloat(rate))) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('labour_rates')
        .update({ rate_per_hour: parseFloat(rate), updated_at: new Date().toISOString(), updated_by: profile.id })
        .eq('trade', trade);
      if (error) throw error;
      flash(`Saved rate for ${trade}`);
      setEditingRate(e => { const n = { ...e }; delete n[trade]; return n; });
      loadData();
    } catch (err) { flash('Error: ' + err.message); }
    finally { setSaving(false); }
  }

  async function saveProjectRates() {
    if (!selectedProject) return;
    setSaving(true);
    try {
      const upserts = (globalRates || [])
        .map(r => ({ trade: r.trade, val: editingProjectRates[r.trade] ?? '' }))
        .filter(x => x.val !== '' && !isNaN(parseFloat(x.val)))
        .map(x => ({
          project_id: selectedProject, trade: x.trade,
          rate_per_hour: parseFloat(x.val),
          updated_at: new Date().toISOString(), updated_by: profile.id,
        }));

      const clearedTrades = (globalRates || [])
        .map(r => r.trade)
        .filter(t => (editingProjectRates[t] ?? '') === '');

      if (upserts.length > 0) {
        const { error } = await supabase.from('project_labour_rates')
          .upsert(upserts, { onConflict: 'project_id,trade' });
        if (error) throw error;
      }
      if (clearedTrades.length > 0) {
        await supabase.from('project_labour_rates')
          .delete().eq('project_id', selectedProject).in('trade', clearedTrades);
      }
      flash('Project rates saved');
      loadProjectRates();
    } catch (err) { flash('Error: ' + err.message); }
    finally { setSaving(false); }
  }

  async function saveUplifts() {
    setSaving(true);
    try {
      await Promise.all([
        supabase.from('commercial_settings')
          .update({ setting_value: parseFloat(settings.materials_uplift_pct) || 0, updated_at: new Date().toISOString() })
          .eq('setting_key', 'materials_uplift_pct'),
        supabase.from('commercial_settings')
          .update({ setting_value: parseFloat(settings.plant_uplift_pct) || 0, updated_at: new Date().toISOString() })
          .eq('setting_key', 'plant_uplift_pct'),
      ]);
      flash('Uplifts saved');
    } catch (err) { flash('Error: ' + err.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={S.spinner}>Loading...</div>;

  return (
    <div>
      {message && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          ...S.alertSuccess, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>
          {message}
        </div>
      )}

      {/* Section 1: Global Default Rates */}
      <div style={{ ...S.card, marginBottom: '24px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '4px' }}>Global Default Rates</h3>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '4px 0 16px' }}>
          These rates apply to all projects unless a project-specific override is set below.
        </p>
        <div style={S.tableWrapper}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Trade</th>
                <th style={S.th}>Rate per Hour</th>
                <th style={S.th}>Last Updated</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {globalRates.map(r => {
                const isEditing = r.trade in editingRate;
                return (
                  <tr key={r.trade}>
                    <td style={S.td}><span style={{ fontWeight: '600' }}>{r.trade}</span></td>
                    <td style={S.td}>
                      {isEditing ? (
                        <input
                          type="number" step="0.01" autoFocus
                          style={{ ...S.input, width: '110px' }}
                          value={editingRate[r.trade]}
                          onChange={e => setEditingRate(er => ({ ...er, [r.trade]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') saveGlobalRate(r.trade, editingRate[r.trade]); if (e.key === 'Escape') setEditingRate(er => { const n = { ...er }; delete n[r.trade]; return n; }); }}
                        />
                      ) : (
                        <span style={{ fontWeight: '600', fontFamily: 'monospace' }}>£{parseFloat(r.rate_per_hour).toFixed(2)}/hr</span>
                      )}
                    </td>
                    <td style={{ ...S.td, fontSize: '13px', color: C.textMuted }}>
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={S.td}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => saveGlobalRate(r.trade, editingRate[r.trade])} disabled={saving} style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }}>Save</button>
                          <button onClick={() => setEditingRate(er => { const n = { ...er }; delete n[r.trade]; return n; })} style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setEditingRate(er => ({ ...er, [r.trade]: String(r.rate_per_hour) }))} style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}>
                          ✏ Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2: Project Rate Overrides */}
      <div style={{ ...S.card, marginBottom: '24px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '4px' }}>Project Rate Overrides</h3>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '4px 0 16px' }}>
          Override rates for a specific project. Leave a field blank to fall back to the global default.
        </p>
        <div style={{ ...S.formGroup, marginBottom: '20px' }}>
          <label style={S.label}>Select Project</label>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} style={{ ...S.select, maxWidth: '420px' }}>
            <option value="">— Choose a project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>)}
          </select>
        </div>

        {selectedProject && (
          <>
            <div style={S.tableWrapper}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Trade</th>
                    <th style={S.th}>Global Default</th>
                    <th style={S.th}>Project Rate (£/hr)</th>
                    <th style={S.th}>Active Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {globalRates.map(r => {
                    const projVal = editingProjectRates[r.trade] ?? '';
                    const hasOverride = projVal !== '' && !isNaN(parseFloat(projVal));
                    const effectiveRate = hasOverride ? parseFloat(projVal) : parseFloat(r.rate_per_hour);
                    return (
                      <tr key={r.trade}>
                        <td style={S.td}><span style={{ fontWeight: '600' }}>{r.trade}</span></td>
                        <td style={{ ...S.td, color: C.textMuted, fontFamily: 'monospace' }}>£{parseFloat(r.rate_per_hour).toFixed(2)}/hr</td>
                        <td style={S.td}>
                          <input
                            type="number" step="0.01"
                            placeholder={`${parseFloat(r.rate_per_hour).toFixed(2)} (default)`}
                            style={{ ...S.input, width: '150px' }}
                            value={projVal}
                            onChange={e => setEditingProjectRates(m => ({ ...m, [r.trade]: e.target.value }))}
                          />
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: '600', color: hasOverride ? '#1e40af' : C.textMuted }}>
                              £{effectiveRate.toFixed(2)}/hr
                            </span>
                            <span style={{
                              fontSize: '11px', fontWeight: '600', padding: '1px 6px', borderRadius: '8px',
                              background: hasOverride ? '#dbeafe' : '#f3f4f6',
                              color: hasOverride ? '#1e40af' : C.textMuted,
                            }}>
                              {hasOverride ? 'Project rate' : 'Default'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={saveProjectRates} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : 'Save Project Rates'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Section 3: Commercial Uplifts */}
      <div style={S.card}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '4px' }}>Commercial Uplifts</h3>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '4px 0 20px' }}>
          Applied on top of cost prices when computing daywork sheet values.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Materials Uplift (%)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="number" step="0.5"
                style={{ ...S.input, width: '90px' }}
                value={settings.materials_uplift_pct}
                onChange={e => setSettings(s => ({ ...s, materials_uplift_pct: e.target.value }))}
              />
              <span style={{ fontSize: '13px', color: C.textMuted }}>Applied to all materials at cost price</span>
            </div>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Plant Uplift (%)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="number" step="0.5"
                style={{ ...S.input, width: '90px' }}
                value={settings.plant_uplift_pct}
                onChange={e => setSettings(s => ({ ...s, plant_uplift_pct: e.target.value }))}
              />
              <span style={{ fontSize: '13px', color: C.textMuted }}>Applied to all plant costs</span>
            </div>
          </div>
        </div>
        <button onClick={saveUplifts} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving...' : 'Save Uplifts'}
        </button>
      </div>
    </div>
  );
}

// --- Email Test Tab ---
function EmailTestTab() {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [toAddress, setToAddress] = useState('simon@prolineinternals.co.uk');
  const [templateChoice, setTemplateChoice] = useState('daywork_submission');

  function buildTestPayload() {
    const portalLink = 'https://proline.project-management.uk';
    const project = { name: 'Test Project', reference: 'TP-2024-001', main_contractor: 'Test MC Ltd' };

    switch (templateChoice) {
      case 'daywork_submission': {
        const dw = { reference: 'DW-TP-0001', date_of_works: '2024-05-01', location: 'Level 3, Grid D4', submitted_at: new Date().toISOString() };
        return dayworkSubmissionEmail(dw, project, { reference: 'IR-TP-001' }, 'Test MC QS', portalLink);
      }
      case 'variation_submission': {
        const v = { reference: 'VAR-TP-001', title: 'Additional partition works', value: 12500, valuation_basis: 'daywork', current_revision: 1, description: 'Additional stud partitioning to Level 3 to accommodate revised M&E routes.' };
        return variationSubmissionEmail(v, project, { reference: 'IR-TP-001' }, 'Test MC QS', portalLink);
      }
      case 'daywork_agreement': {
        const dw = { reference: 'DW-TP-0001', date_of_works: '2024-05-01' };
        return dayworkAgreementEmail(dw, project, 'John Smith (MC QS)', new Date().toISOString());
      }
      case 'variation_agreement': {
        const v = { reference: 'VAR-TP-001', title: 'Additional partition works', value: 12500 };
        return variationAgreementEmail(v, project, 'John Smith (MC QS)', new Date().toISOString(), 11750);
      }
      default:
        return null;
    }
  }

  async function handleSend() {
    const payload = buildTestPayload();
    if (!payload) return;
    setSending(true);
    setResult(null);
    try {
      await sendEmail({ to: toAddress, subject: payload.subject, htmlBody: payload.htmlBody });
      setResult({ ok: true, message: `Email sent to ${toAddress}` });
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setSending(false);
    }
  }

  const TEMPLATE_LABELS = {
    daywork_submission: 'Daywork Submission (to MC QS)',
    variation_submission: 'Variation Submission (to MC QS)',
    daywork_agreement: 'Daywork Agreement Confirmation',
    variation_agreement: 'Variation Agreement Confirmation',
  };

  return (
    <div>
      <div style={{ ...S.card, marginBottom: '24px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '4px' }}>Email Service</h3>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '4px 0 20px' }}>
          Emails are sent via the Proline API proxy on port 3001, which forwards to Microsoft Graph.
          Configure <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: '3px' }}>/opt/proline-api/.env</code> with your Microsoft Graph credentials.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Template</label>
            <select value={templateChoice} onChange={e => setTemplateChoice(e.target.value)} style={S.select}>
              {Object.entries(TEMPLATE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Send to</label>
            <input
              type="email"
              value={toAddress}
              onChange={e => setToAddress(e.target.value)}
              style={S.input}
              placeholder="recipient@example.com"
            />
          </div>
        </div>

        <button
          onClick={handleSend}
          disabled={sending || !toAddress}
          style={{ ...S.btn, ...S.btnPrimary, opacity: (sending || !toAddress) ? 0.7 : 1 }}
        >
          {sending ? 'Sending…' : 'Send Test Email'}
        </button>

        {result && (
          <div style={{ ...result.ok ? S.alertSuccess : S.alertError, marginTop: '16px' }}>
            {result.message}
          </div>
        )}
      </div>

      <div style={S.card}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Configuration</h3>
        <div style={{ ...S.tableWrapper }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Variable</th>
                <th style={S.th}>Description</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['GRAPH_TENANT_ID', 'Azure AD tenant ID (Directory ID)'],
                ['GRAPH_CLIENT_ID', 'App registration client ID'],
                ['GRAPH_CLIENT_SECRET', 'App registration client secret'],
                ['GRAPH_FROM_EMAIL', 'The Microsoft 365 mailbox to send from (e.g. notifications@company.com)'],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={S.td}><code style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 6px', borderRadius: '3px' }}>{k}</code></td>
                  <td style={{ ...S.td, fontSize: '13px', color: C.textMuted }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: '12px', color: C.textMuted, marginTop: '12px', marginBottom: 0 }}>
          Set these in <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: '3px' }}>/opt/proline-api/.env</code> then run{' '}
          <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: '3px' }}>systemctl restart proline-api</code>.
        </p>
      </div>
    </div>
  );
}

// --- Main Admin Page ---
export default function Admin({ navigate, params }) {
  const initialTab = params?.tab || 'approvals';
  const [activeTab, setActiveTab] = useState(initialTab);

  const tabs = [
    { id: 'approvals', label: 'Pending Approvals' },
    { id: 'users', label: 'All Users' },
    { id: 'projects', label: 'Projects' },
    { id: 'assignments', label: 'Assignments' },
    { id: 'labour-rates', label: 'Labour Rates' },
    { id: 'email', label: 'Email' },
  ];

  return (
    <div style={S.pageContainer}>
      <div style={S.pageHeader}>
        <h1 style={S.pageTitle}>Admin Panel</h1>
      </div>

      <div style={S.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{ ...S.tab, ...(activeTab === tab.id ? S.tabActive : {}) }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'approvals' && <PendingApprovalsTab />}
      {activeTab === 'users' && <AllUsersTab />}
      {activeTab === 'projects' && <ProjectsTab navigate={navigate} />}
      {activeTab === 'assignments' && <AssignmentsTab />}
      {activeTab === 'labour-rates' && <LabourRatesTab />}
      {activeTab === 'email' && <EmailTestTab />}
    </div>
  );
}
